// Typed client for the M1 HTTP API. Same-origin fetch carries the session
// cookie; the browser's `Origin` header satisfies the server's CSRF check.

import { reportApi } from "./connState.ts";
import type { FullGame } from "./game/types.ts";

export type Role = "superuser" | "admin" | "player";

export interface User {
  id: string;
  username: string;
  email: string;
  role: Role;
}

/** A row in the member's game list (home + history screens). */
export interface GameListItem {
  lifecycle?: "waiting" | "active" | "paused" | "finished";
  clock_mode?: "timed" | "untimed";
  id: string;
  opponent: string;
  color: "white" | "black";
  tc: string;
  speed: string | null;
  rated: boolean;
  status: string;
  result: "playing" | "won" | "lost" | "draw" | "aborted";
  started_at: number | null;
  finished_at: number | null;
  rating_diff: number | null;
}

/** One leaderboard entry within a speed. */
export interface RankEntry {
  username: string;
  rating: number;
  provisional: boolean;
  games: number;
}

export interface Leaderboard {
  boards: Record<string, RankEntry[]>; // keyed by speed ("blitz" | "rapid")
}

export interface PlayerRating {
  rating: number;
  provisional: boolean;
}

export interface PlayerProfile {
  username: string;
  created_at: number;
  ratings: Record<string, PlayerRating>; // keyed by speed
  is_self: boolean;
  /** Whether the viewer is allowed to challenge this member (group gate). */
  can_challenge: boolean;
  h2h: { wins: number; losses: number; draws: number };
}

// --- Admin dashboard (plan §admin) ---------------------------------------

export interface AdminUserRow {
  id: string;
  username: string;
  email: string;
  role: Role;
  banned: boolean;
  created_at: number;
  inviter: string | null;
}

export interface AdminGroup {
  id: string;
  name: string;
  kind: "static" | "live";
  spec: string | null;
  description: string | null;
  created_at: number;
  member_count: number;
}

export interface GroupMemberList {
  kind: string;
  /** True for viewer-relative live groups (invite-distance) — no fixed roster. */
  relative: boolean;
  members: { id: string; username: string }[];
}

export interface AdminSettings {
  admin_can_delegate: boolean;
  membership_open: boolean;
  membership_notify: string;
}

export interface GatewayAdminProviderStatus {
  provider: string;
  account_id: string;
  configured: boolean;
  state: "running" | "degraded" | "disabled" | "unconfigured";
  detail: string | null;
  enabled: boolean;
  control_updated_at: number | null;
  last_probe_at: number | null;
  last_success_at: number | null;
  last_error: string | null;
  last_error_at: number | null;
}

export interface GatewayAdminQueueItem {
  id: string;
  kind: string;
  state: string;
  attempt_count: number;
  last_error: string | null;
  created_at: number;
  next_attempt_at?: number;
  updated_at?: number;
  sensitivity?: string;
  retryable?: boolean;
}

export interface GatewayAdminStatus {
  providers: GatewayAdminProviderStatus[];
  queue: {
    depth: number;
    oldest_age_secs: number | null;
    dead_letter_count: number;
  };
  queued: GatewayAdminQueueItem[];
  dead_letters: GatewayAdminQueueItem[];
  alerts: { kind: string; provider?: string; account_id?: string }[];
}

/** The signed-in member's own editable profile (self only; includes phone). */
export interface MyProfile {
  username: string;
  email: string;
  phone: string | null;
}

export interface GatewayBinding {
  id: string;
  account_id: string;
  display_hint: string | null;
  enabled: boolean;
  last_seen_at: number | null;
}

export interface GatewayProviderSettings {
  provider: string;
  configured: boolean;
  health: { state: string; account_id: string; detail?: string }[];
  bindings: GatewayBinding[];
  virtual_binding: { address?: string; verified: boolean } | null;
}

export interface GatewaySettings {
  email: { address: string; verified: boolean };
  phone: { value: string | null; verified: false };
  providers: GatewayProviderSettings[];
  match_preferences: {
    provider: string;
    enabled: boolean;
    priority: number;
  }[];
}

/** One entry in the full member directory (plan §7.2). Carries `id` so live
 *  presence dots can light up. */
export interface DirectoryEntry {
  id: string;
  username: string;
  ratings: Record<string, PlayerRating>; // keyed by speed ("blitz" | "rapid")
  last_played_at: number | null;
}

export interface InviteInfo {
  can_notify: boolean;
  challenger: string | null;
  email: string | null;
  tc: string;
  color: string;
  rated: boolean;
  status: string;
  expired: boolean;
  usable: boolean;
  game_id: string | null;
  ready_ping: boolean;
  ready_note: string | null;
  /** The invited email already belongs to a member → offer sign-in, not signup. */
  member: boolean;
  /** That member's username, when `member` is true (for a friendly greeting). */
  member_username: string | null;
}

export type GatewayDeliveryState =
  | "queued"
  | "sent"
  | "acknowledged_in_app"
  | "no_route"
  | "failed";

/** Provider-neutral delivery receipt. `provider` is optional display detail,
 * never a product-logic discriminator. */
export interface GatewayReceipt {
  state: GatewayDeliveryState;
  provider?: string;
  notification_id?: string;
}

export interface ChallengeCreated {
  ok: boolean;
  challenge_id: string;
  token: string;
  delivery: GatewayReceipt;
}

/** A pending challenge addressed to me (UC-7). */
export interface IncomingChallenge {
  token: string;
  challenger: string;
  tc: string;
  created_at: number;
}

/** A challenge *I* sent, for the Activity screen (item 6). */
export interface SentChallenge {
  challenge_id: string;
  /** Member username, else the raw invite email, else "Someone". */
  opponent: string;
  /** "created" (pending) | "accepted" | "declined" | "expired" | "canceled". */
  status: string;
  game_id: string | null;
  created_at: number;
  /** Whether the bound member is online now; null for email-only invites. */
  online: boolean | null;
  /** True when they're sitting at an accepted game's board, waiting for me. */
  waiting: boolean;
}

export interface InviteCreated {
  ok: boolean;
  challenge_id: string;
  invite_url: string;
}

export interface Registered {
  ok: boolean;
  username: string;
  game_id: string;
}

/** An error carrying the HTTP status and the server's `{error}` message. */
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

export async function req<T>(method: string, url: string, body?: unknown): Promise<T> {
  const hasBody = body !== undefined;
  let res: Response;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    res = await fetch(url, {
      method,
      headers: hasBody ? { "content-type": "application/json", "x-chess-protocol": "2" } : { "x-chess-protocol": "2" },
      body: hasBody ? JSON.stringify(body) : undefined,
      credentials: "same-origin",
      signal: controller.signal,
    });
  } catch (e) {
    reportApi(false); // network-level failure — the server is unreachable (item 4)
    if (controller.signal.aborted) throw new Error("The request timed out. Please retry to check its result.");
    throw e;
  } finally {
    clearTimeout(timeout);
  }
  // A 5xx means the server is unhealthy; a 4xx is a normal app error (the server
  // answered, so it's up). Either way a response clears an "unreachable" state.
  reportApi(res.status < 500);
  const text = await res.text();
  let data: unknown = {};
  try { if (text) data = JSON.parse(text); }
  catch {
    // Proxies can return HTML for rate limits or outages. Keep the HTTP status
    // and do not display their raw response or lose command-retry semantics.
    if (res.ok) throw new ApiError(res.status, "The server returned an unreadable response. Please retry.");
  }
  if (!res.ok) {
    const msg =
      (data && typeof data === "object" && "error" in data && typeof data.error === "string"
        ? data.error
        : undefined) ?? (res.status === 429 ? "Too many requests. Wait a moment, then try again." : `Request failed (HTTP ${res.status}). Please retry.`);
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

export const api = {
  createGameLink: (tc: string, color: string, rated: boolean) => req<InviteCreated>("POST", "/api/invite/link", { tc, color, rated }),
  me: () => req<User>("GET", "/api/auth/me"),
  requestEmailCode: (email: string) => req<{ ok: boolean; request_id: string; expires_in: number; resend_after: number }>("POST", "/api/auth/email/request", { email }),
  verifyEmailCode: (requestId: string, code: string) => req<User>("POST", "/api/auth/email/verify", { request_id: requestId, code }),
  login: (login: string, password: string) =>
    req<User>("POST", "/api/auth/login", { login, password }),
  requestLoginCode: (identifier: string, provider: "email" | "telegram") =>
    req<{ ok: boolean; request_id: string; expires_in: number }>(
      "POST",
      "/api/auth/code/request",
      { identifier, provider },
    ),
  verifyLoginCode: (requestId: string, code: string) =>
    req<User>("POST", "/api/auth/code/verify", {
      request_id: requestId,
      code,
    }),
  logout: () => req<{ ok: boolean }>("POST", "/api/auth/logout", {}),

  /** Request a password-reset link. Always resolves ok (no account-existence leak). */
  forgotPassword: (login: string) =>
    req<{ ok: boolean }>("POST", "/api/auth/forgot", { login }),
  /** Set a new password from a reset token; signs the member in on success. */
  resetPassword: (token: string, password: string) =>
    req<{ ok: boolean; username: string }>("POST", "/api/auth/reset", { token, password }),

  createInvite: (
    email: string,
    tc: string,
    color: string,
    rated: boolean,
    readyPing = false,
    readyNote = "",
  ) =>
    req<InviteCreated>("POST", "/api/invite", {
      email,
      tc,
      color,
      rated,
      ready_ping: readyPing,
      ready_note: readyNote,
    }),

  /** Fire a Ready-signal (ready/wait/later) to a challenge's counterpart (UC-3/UC-6). */
  sendReady: (challengeId: string, kind: "ready" | "wait" | "later", note?: string) =>
    req<{ ok: boolean; delivery: GatewayReceipt }>(
      "POST",
      `/api/challenge/${encodeURIComponent(challengeId)}/ready`,
      { kind, note },
    ),

  /** Challenge an existing member directly, by username (UC-7). */
  challengeMember: (opponent: string, tc: string, color: string, rated: boolean) =>
    req<ChallengeCreated>("POST", "/api/challenge", { opponent, tc, color, rated }),

  /** Pending challenges addressed to me (UC-7), capped for the home page; `has_more`
   *  is set when older pending challenges were left off (plan item 3). */
  incomingChallenges: () =>
    req<{ challenges: IncomingChallenge[]; has_more: boolean }>("GET", "/api/challenges/incoming"),
  /** Challenges I sent + their outcome, newest first (item 6, Activity screen).
   *  Paged with `has_more` for a "Load more" button. */
  sentChallenges: (limit?: number, offset?: number) => {
    const p = new URLSearchParams();
    if (limit != null) p.set("limit", String(limit));
    if (offset != null) p.set("offset", String(offset));
    const qs = p.toString();
    return req<{ challenges: SentChallenge[]; has_more: boolean }>(
      "GET",
      `/api/challenges/sent${qs ? `?${qs}` : ""}`,
    );
  },
  resolveInvite: (token: string) =>
    req<InviteInfo>("GET", `/api/invite/${encodeURIComponent(token)}`),
  acceptInvite: (token: string) =>
    req<{ ok: boolean; game_id: string }>("POST", `/api/invite/${encodeURIComponent(token)}/accept`, {}),
  /** Decline an invite addressed to me (item 6); signals the challenger. */
  declineInvite: (token: string) =>
    req<{ ok: boolean }>("POST", `/api/invite/${encodeURIComponent(token)}/decline`, {}),

  register: (token: string, username: string, password: string) =>
    req<Registered>("POST", "/api/onboard/register", { token, username, password }),

  getGame: (id: string) => req<FullGame>("GET", `/api/game/${encodeURIComponent(id)}`),
  /** Paged game list. `since` is unix seconds (time-horizon filter). Returns the
   *  page plus `has_more` so the caller can show a "Load more" button (plan §6). */
  listGames: (limit?: number, offset?: number, since?: number, ongoing = false) => {
    const p = new URLSearchParams();
    if (limit != null) p.set("limit", String(limit));
    if (offset != null) p.set("offset", String(offset));
    if (since != null) p.set("since", String(since));
    if (ongoing) p.set("ongoing", "true");
    const qs = p.toString();
    return req<{ games: GameListItem[]; has_more: boolean }>("GET", `/api/games${qs ? `?${qs}` : ""}`);
  },

  leaderboard: (speed?: string) =>
    req<Leaderboard>("GET", `/api/leaderboard${speed ? `?speed=${encodeURIComponent(speed)}` : ""}`),
  /** One page of the member directory (all members except self), for the Players
   *  screen. `q` is a case-insensitive username prefix search. Returns the page
   *  plus `has_more` for a "Load more" button (plan item 1). */
  players: (limit?: number, offset?: number, q?: string) => {
    const p = new URLSearchParams();
    if (limit != null) p.set("limit", String(limit));
    if (offset != null) p.set("offset", String(offset));
    if (q) p.set("q", q);
    const qs = p.toString();
    return req<{ players: DirectoryEntry[]; has_more: boolean }>("GET", `/api/players${qs ? `?${qs}` : ""}`);
  },
  player: (name: string) => req<PlayerProfile>("GET", `/api/player/${encodeURIComponent(name)}`),

  myProfile: () => req<MyProfile>("GET", "/api/profile"),
  updatePhone: (phone: string) => req<{ ok: boolean; phone: string | null }>("PATCH", "/api/profile", { phone }),

  gatewaySettings: () => req<GatewaySettings>("GET", "/api/gateway/settings"),
  requestGatewayLink: (provider: string) =>
    req<{
      provider: string;
      account_id: string;
      code: string;
      expires_at: number;
      /** One-tap `t.me/<bot>?start=<code>` link; null when the bot username
       *  can't be resolved, in which case only `instructions` apply. */
      deep_link: string | null;
      instructions: string;
    }>("POST", `/api/gateway/bindings/${encodeURIComponent(provider)}/link`, {}),
  /** Mail a "connect this channel" button to the member's account address —
   *  for reading Chess on a desktop while the messenger lives on the phone. */
  mailGatewayConnectInvite: (provider: string) =>
    req<{ ok: boolean; email: string }>(
      "POST",
      `/api/gateway/bindings/${encodeURIComponent(provider)}/link/email`,
      {},
    ),
  unlinkGateway: (provider: string) =>
    req<{ ok: boolean; unlinked: boolean }>(
      "DELETE",
      `/api/gateway/bindings/${encodeURIComponent(provider)}`,
    ),
  updateMatchPreferences: (order: string[], disabled: string[]) =>
    req<{ ok: boolean }>("PATCH", "/api/gateway/preferences/match", {
      order,
      disabled,
    }),

  // --- Admin dashboard ---------------------------------------------------
  /** One page of the member roster (plan item 4). `q` matches a username or email
   *  prefix; `role`/`banned` narrow further. Returns the page plus `has_more`. */
  adminUsers: (opts?: {
    limit?: number;
    offset?: number;
    q?: string;
    role?: string;
    banned?: boolean;
  }) => {
    const p = new URLSearchParams();
    if (opts?.limit != null) p.set("limit", String(opts.limit));
    if (opts?.offset != null) p.set("offset", String(opts.offset));
    if (opts?.q) p.set("q", opts.q);
    if (opts?.role) p.set("role", opts.role);
    if (opts?.banned != null) p.set("banned", opts.banned ? "1" : "0");
    const qs = p.toString();
    return req<{ users: AdminUserRow[]; has_more: boolean }>(
      "GET",
      `/api/admin/users${qs ? `?${qs}` : ""}`,
    );
  },
  adminSetRole: (id: string, role: "admin" | "player") =>
    req<{ ok: boolean; role: string }>("PATCH", `/api/admin/users/${encodeURIComponent(id)}/role`, { role }),
  adminBan: (id: string) => req<{ ok: boolean }>("POST", `/api/admin/users/${encodeURIComponent(id)}/ban`, {}),
  adminUnban: (id: string) => req<{ ok: boolean }>("POST", `/api/admin/users/${encodeURIComponent(id)}/unban`, {}),
  adminDeleteUser: (id: string) => req<{ ok: boolean }>("DELETE", `/api/admin/users/${encodeURIComponent(id)}`),

  adminGroups: () => req<{ groups: AdminGroup[] }>("GET", "/api/admin/groups"),
  adminCreateGroup: (name: string, kind: "static" | "live", spec?: string) =>
    req<{ ok: boolean; id: string }>("POST", "/api/admin/groups", { name, kind, spec }),
  adminDeleteGroup: (id: string) =>
    req<{ ok: boolean }>("DELETE", `/api/admin/groups/${encodeURIComponent(id)}`),
  adminGroupMembers: (id: string) =>
    req<GroupMemberList>("GET", `/api/admin/groups/${encodeURIComponent(id)}/members`),
  adminAddGroupMember: (id: string, userId: string) =>
    req<{ ok: boolean }>("POST", `/api/admin/groups/${encodeURIComponent(id)}/members`, { user_id: userId }),
  adminRemoveGroupMember: (id: string, userId: string) =>
    req<{ ok: boolean }>(
      "DELETE",
      `/api/admin/groups/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
    ),

  adminSettings: () => req<AdminSettings>("GET", "/api/admin/settings"),
  adminSetSettings: (
    patch: Partial<{ admin_can_delegate: boolean; membership_open: boolean; membership_notify: string }>,
  ) => req<AdminSettings & { ok: boolean }>("PATCH", "/api/admin/settings", patch),
  gatewayAdminStatus: () =>
    req<GatewayAdminStatus>("GET", "/api/gateway/admin/status"),
  gatewayAdminSetProvider: (
    provider: string,
    accountId: string,
    enabled: boolean,
  ) =>
    req<{ ok: boolean; provider: string; account_id: string; enabled: boolean }>(
      "PATCH",
      `/api/gateway/admin/providers/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}`,
      { enabled },
    ),
  gatewayAdminRetry: (notificationId: string) =>
    req<{ ok: boolean }>(
      "POST",
      `/api/gateway/admin/dead-letters/${encodeURIComponent(notificationId)}/retry`,
      {},
    ),
  /** Superuser-only dev reset (wipes everything but the superuser). */
  adminReset: () => req<{ ok: boolean }>("POST", "/api/admin/reset", { confirm: "RESET" }),

  // --- Public bootstrap config + membership application (§6.3/§7) -----------
  /** Public config gate — is the "Become a member" form open? */
  getConfig: () =>
    req<{ membership_open: boolean; code_login_enabled: boolean; email_signin_enabled: boolean; registration_mode: "closed" | "public" | "private_invite" }>(
      "GET",
      "/api/config",
    ),
  /** Submit a membership application (emails the superuser's configured address). */
  apply: (b: { name: string; email: string; experience: string; message: string }) =>
    req<{ ok: boolean }>("POST", "/api/apply", b),
};
